import { IconChevronRight } from "@tabler/icons-react";
import type { FC, PropsWithChildren } from "react";
import {
  Button as AriaButton,
  TreeItem,
  TreeItemContent,
} from "react-aria-components";
import { tv } from "tailwind-variants";

//
// Main section node component.
//

interface AssessmentSectionNodeProps extends PropsWithChildren {
  id: string;
  textValue: string;
}

const sectionItemStyle = tv({
  base: `
    group
    col-span-full
    flex
    cursor-pointer
    items-center
    gap-3
    border-t
    border-r
    border-l
    border-border-light
    bg-gray-100
    px-3
    py-2
    first:rounded-t-lg
    last:rounded-b-lg
    last:border-b
    data-[react-aria-pressable]:cursor-pointer
    data-[react-aria-pressable]:hover:bg-blue-50
  `,
});

export const AssessmentSectionNode: FC<AssessmentSectionNodeProps> = (
  props,
) => {
  const { id, textValue, children } = props;
  return (
    <TreeItem
      id={id}
      textValue={textValue || "Section"}
      className={sectionItemStyle()}
    >
      {children}
    </TreeItem>
  );
};

//
// Header component.
//

export const AssessmentSectionNodeContent: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  return <TreeItemContent>{children}</TreeItemContent>;
};

//
// Title component.
//

interface AssessmentSectionNodeTitleProps extends PropsWithChildren {
  isNotApplicable?: boolean;
}

const titleStyle = tv({
  base: `
    flex
    min-w-0
    items-center
    gap-2
    group-aria-[level=2]:pl-5
    group-aria-[level=3]:pl-10
    group-aria-[level=4]:pl-14
  `,
});

const titleTextStyle = tv({
  base: `
    truncate
    text-sm
    font-semibold
  `,
  variants: {
    isNotApplicable: {
      true: `
        text-muted-foreground
      `,
    },
  },
});

export const AssessmentSectionNodeTitle: FC<AssessmentSectionNodeTitleProps> = (
  props,
) => {
  const { isNotApplicable, children } = props;
  return (
    <div className={titleStyle()}>
      <AriaButton slot="chevron">
        <IconChevronRight className="size-4 text-muted-foreground transition-transform group-aria-expanded:rotate-90" />
      </AriaButton>
      <span className={titleTextStyle({ isNotApplicable })}>
        {children || "Section"}
      </span>
    </div>
  );
};

//
// Grading component.
//

export const AssessmentSectionNodeGrading: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  return (
    <>
      <div className="flex-1" />
      <div className="flex gap-3">{children}</div>
    </>
  );
};
